import { collection, doc, getDocs, updateDoc, deleteDoc, query, where, writeBatch, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import { ReviewQuestion, ReviewAttempt } from '../types';

const getBank = (userId: string) =>
    collection(db, 'users', userId, 'reviewQuestions');

const getAttempts = (userId: string) =>
    collection(db, 'users', userId, 'reviewAttempts');

// SRS intervals (ms): wrong=1h, correct 1st=1d, 2nd=3d, 3rd+=7d
const nextDueAfter = (wasCorrect: boolean, timesCorrectAfter: number): number => {
    const HOUR = 60 * 60 * 1000;
    const DAY = 24 * HOUR;
    if (!wasCorrect) return Date.now() + HOUR;
    if (timesCorrectAfter === 1) return Date.now() + DAY;
    if (timesCorrectAfter === 2) return Date.now() + 3 * DAY;
    return Date.now() + 7 * DAY;
};

export const loadReviewQuestions = async (userId: string, notebookId?: string): Promise<ReviewQuestion[]> => {
    if (userId === 'guest') return [];
    const baseQuery = notebookId
        ? query(getBank(userId), where('notebookId', '==', notebookId))
        : getBank(userId);
    const snap = await getDocs(baseQuery);
    const now = Date.now();

    // Only load active questions that are currently due
    const questions = snap.docs
        .map(d => d.data() as ReviewQuestion)
        .filter(q => {
            if (q.status === 'archived' || q.status === 'stale') return false;
            if (q.nextDueAt !== null && q.nextDueAt !== undefined && q.nextDueAt > now) return false;
            return true;
        });

    // Priority sort:
    // 1. Never asked (lastAskedAt === null) first
    // 2. Weakest (lowest correct ratio) next
    // 3. Oldest last-asked last
    return questions.sort((a, b) => {
        const aRatio = a.timesAsked === 0 ? -Infinity : a.timesCorrect / a.timesAsked;
        const bRatio = b.timesAsked === 0 ? -Infinity : b.timesCorrect / b.timesAsked;
        if (aRatio !== bRatio) return aRatio - bRatio;
        const aTime = a.lastAskedAt ?? 0;
        const bTime = b.lastAskedAt ?? 0;
        return aTime - bTime;
    });
};

export const recordQuestionAnswer = async (
    userId: string,
    questionId: string,
    wasCorrect: boolean
): Promise<void> => {
    if (userId === 'guest') return;
    const snap = await getDocs(query(getBank(userId), where('id', '==', questionId)));
    if (snap.empty) return;

    const q = snap.docs[0].data() as ReviewQuestion;
    const newTimesCorrect = q.timesCorrect + (wasCorrect ? 1 : 0);
    const newTimesAsked = q.timesAsked + 1;
    const newNextDueAt = nextDueAfter(wasCorrect, newTimesCorrect);

    await updateDoc(snap.docs[0].ref, {
        lastAskedAt: Date.now(),
        timesAsked: newTimesAsked,
        timesCorrect: newTimesCorrect,
        nextDueAt: newNextDueAt,
    });

    // Write ReviewAttempt record
    const attempt: Omit<ReviewAttempt, 'id'> = {
        questionId,
        nodeId: q.nodeId,
        notebookId: q.notebookId,
        selectedAnswerIndex: -1, // caller doesn't pass this yet; update if needed
        correct: wasCorrect,
        createdAt: Date.now(),
    };
    try {
        await addDoc(getAttempts(userId), attempt);
    } catch {
        // Non-critical — don't fail the answer recording
    }
};

export const deleteQuestionsForNode = async (
    userId: string,
    nodeId: string
): Promise<void> => {
    if (userId === 'guest') return;
    const snap = await getDocs(query(getBank(userId), where('nodeId', '==', nodeId)));
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
};

export const nodeHasReviewQuestions = async (
    userId: string,
    nodeId: string
): Promise<boolean> => {
    if (userId === 'guest') return false;
    const snap = await getDocs(query(getBank(userId), where('nodeId', '==', nodeId)));
    return !snap.empty;
};

export const loadQuestionsForNode = async (userId: string, nodeId: string): Promise<ReviewQuestion[]> => {
    if (userId === 'guest') return [];
    const snap = await getDocs(query(getBank(userId), where('nodeId', '==', nodeId)));
    const now = Date.now();
    return snap.docs
        .map(d => d.data() as ReviewQuestion)
        .filter(q => q.status !== 'archived')
        .sort((a, b) => {
            const aDue = !a.nextDueAt || a.nextDueAt <= now;
            const bDue = !b.nextDueAt || b.nextDueAt <= now;
            if (aDue !== bDue) return aDue ? -1 : 1;
            return (a.generatedAt ?? 0) - (b.generatedAt ?? 0);
        });
};

export const loadAllReviewQuestions = async (userId: string, notebookId?: string): Promise<ReviewQuestion[]> => {
    if (userId === 'guest') return [];
    const baseQuery = notebookId
        ? query(getBank(userId), where('notebookId', '==', notebookId))
        : getBank(userId);
    const snap = await getDocs(baseQuery);
    return snap.docs
        .map(d => d.data() as ReviewQuestion)
        .filter(q => q.status !== 'archived')
        .sort((a, b) => {
            // Sort: due/overdue first, then by nextDueAt ascending
            const now = Date.now();
            const aDue = !a.nextDueAt || a.nextDueAt <= now;
            const bDue = !b.nextDueAt || b.nextDueAt <= now;
            if (aDue !== bDue) return aDue ? -1 : 1;
            return (a.nextDueAt ?? 0) - (b.nextDueAt ?? 0);
        });
};

export const loadAttemptsForNode = async (userId: string, nodeId: string): Promise<ReviewAttempt[]> => {
    if (userId === 'guest') return [];
    const snap = await getDocs(query(getAttempts(userId), where('nodeId', '==', nodeId)));
    return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as ReviewAttempt))
        .sort((a, b) => b.createdAt - a.createdAt);
};

export const countQuestionsForNode = async (
    userId: string,
    nodeId: string
): Promise<number> => {
    if (userId === 'guest') return 0;
    const snap = await getDocs(query(
        getBank(userId),
        where('nodeId', '==', nodeId),
        where('status', '==', 'active')
    ));
    return snap.size;
};
